import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import MergeTypeIcon from '@mui/icons-material/MergeType';
import RateReviewOutlinedIcon from '@mui/icons-material/RateReviewOutlined';
import RocketLaunchOutlinedIcon from '@mui/icons-material/RocketLaunchOutlined';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import BugReportOutlinedIcon from '@mui/icons-material/BugReportOutlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import WorkspacesOutlinedIcon from '@mui/icons-material/WorkspacesOutlined';
import SpaOutlinedIcon from '@mui/icons-material/SpaOutlined';
import RocketOutlinedIcon from '@mui/icons-material/RocketOutlined';
import WorkspacePremiumOutlinedIcon from '@mui/icons-material/WorkspacePremiumOutlined';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import { SKILL_DEVELOPMENT } from '../../data/programs';
import { formatInr } from '../../data/roadmapSession';

// The team workspace feed: what a normal day on an engineering team looks like
const events = [
  { icon: MergeTypeIcon, tone: 'green', who: 'Teammate', action: 'merged', target: 'PR #142 · Pagination for the orders API', tag: 'Approved' },
  { icon: RateReviewOutlinedIcon, tone: 'amber', who: 'Senior engineer', action: 'requested changes on', target: 'PR #145 · Cache product search', tag: 'Review' },
  { icon: RocketLaunchOutlinedIcon, tone: 'cyan', who: 'CI', action: 'deployed', target: 'v1.8.0 to production', tag: 'Live' },
  { icon: GroupsOutlinedIcon, tone: 'purple', who: 'Team', action: 'daily standup', target: 'Yesterday, today, blockers', tag: '10:00 AM' },
  { icon: BugReportOutlinedIcon, tone: 'pink', who: 'You', action: 'picked up', target: 'BUG-88 · Checkout total rounding', tag: 'In progress' },
  { icon: DescriptionOutlinedIcon, tone: 'cyan', who: 'Tech lead', action: 'shared', target: 'Design doc · Notifications service', tag: 'RFC' }
];

const VISIBLE_EVENTS = 4;
const FEED_MS = 2600;

const steps = [
  { title: 'Join an engineering team', text: 'Work alongside other engineers in a small team led by a senior engineer.' },
  { title: 'Pick tickets from a real backlog', text: 'Features, bugs and refactors, estimated and planned in sprints.' },
  { title: 'Ship through code review', text: 'Every pull request is reviewed the way it would be at a product company.' },
  { title: 'Deploy and demo', text: 'Release to production, demo at sprint review, and own what you built.' }
];

// Headline promises, shown right under the section heading
const highlights = [
  { icon: SpaOutlinedIcon, tone: 'green', title: 'No experience required', text: 'Start where you are' },
  { icon: RocketOutlinedIcon, tone: 'cyan', title: 'Real projects', text: 'Not just interview prep' },
  { icon: WorkspacePremiumOutlinedIcon, tone: 'amber', title: 'Internship certificate', text: 'On completion' }
];

const skills = ['Git & pull requests', 'Code reviews', 'Agile sprints', 'CI/CD & deployments', 'Design docs', 'Interview-ready project stories'];

// What the price card lists, so the price reads as a package rather than a bare number
const includes = [
  '3 months on a real engineering team',
  'Senior engineer reviews on every PR',
  'Internship certificate on completion'
];

const savings = SKILL_DEVELOPMENT.listPriceInr - SKILL_DEVELOPMENT.priceInr;
const discountPct = Math.round((savings / SKILL_DEVELOPMENT.listPriceInr) * 100);

const prefersReducedMotion = () => window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

// A mock team workspace whose activity feed keeps moving, like a real team's
const Workspace = () => {
  const [offset, setOffset] = useState(0);

  useEffect(() => {
    if (prefersReducedMotion()) return undefined;
    const timer = setInterval(() => setOffset((o) => (o + 1) % events.length), FEED_MS);
    return () => clearInterval(timer);
  }, []);

  // Newest first: each tick, the next event slides in at the top
  const visible = Array.from({ length: VISIBLE_EVENTS }, (_, i) => {
    const index = (offset - i + events.length) % events.length;
    return { ...events[index], key: `${index}-${offset - i}` };
  });

  return (
    <div className="sd-workspace" aria-hidden="true">
      <div className="sd-ws-bar">
        <span className="sd-dots"><i /><i /><i /></span>
        <span className="sd-ws-title"><WorkspacesOutlinedIcon sx={{ fontSize: 15 }} /> team-workspace</span>
        <span className="sd-live"><i /> Live</span>
      </div>

      <div className="sd-sprint">
        <div className="sd-sprint-head">
          <strong>Sprint 6</strong>
          <span>Day 4 of 10</span>
        </div>
        <div className="sd-sprint-bar"><span /></div>
        <div className="sd-board">
          <div><span>To do</span><strong>4</strong></div>
          <div><span>In progress</span><strong>3</strong></div>
          <div><span>In review</span><strong>2</strong></div>
          <div className="done"><span>Done</span><strong>9</strong></div>
        </div>
      </div>

      <ul className="sd-feed">
        {visible.map(({ key, icon: Icon, tone, who, action, target, tag }, i) => (
          <li key={key} className={`sd-event sd-tone-${tone} ${i === 0 ? 'is-new' : ''}`}>
            <span className="sd-event-icon"><Icon sx={{ fontSize: 17 }} /></span>
            <div className="sd-event-text">
              <p><strong>{who}</strong> {action}</p>
              <span>{target}</span>
            </div>
            <em>{tag}</em>
          </li>
        ))}
      </ul>
    </div>
  );
};

const SkillDevelopment = () => (
  <section className="home-section skilldev" id="skill-development">
    <div className="container">
      <div className="home-section-head" data-reveal>
        <span className="section-eyebrow">Join the Real Engineering team</span>
        <h2>
          Work like an engineer <span className="text-gradient">before you&apos;re hired as one</span>
        </h2>
        <p>
          Join a team of engineers and ship real work the way product companies do: tickets, pull requests, code
          reviews, standups and deployments. Build the habits interviewers look for, and the stories to prove them.
        </p>
      </div>

      <ul className="sd-highlights">
        {highlights.map(({ icon: Icon, tone, title, text }, i) => (
          <li key={title} className={`sd-highlight sd-tone-${tone}`} data-reveal style={{ '--i': i }}>
            <Icon className="sd-highlight-icon" sx={{ fontSize: 28 }} />
            <div>
              <strong>{title}</strong>
              <p>{text}</p>
            </div>
          </li>
        ))}
      </ul>

      <div className="sd-grid">
        <div data-reveal>
          <Workspace />
        </div>

        <div className="sd-pitch" id="program-details" data-reveal style={{ '--i': 1 }}>
          <div className="sd-level">
            <strong>3 months</strong>
            <span>
              <b>All you need is to work with real engineers for 3 months.</b> Come out working like an engineer with
              1–3 years of experience, with real work to talk about in interviews.
            </span>
          </div>

          <ol className="sd-steps">
            {steps.map((s, i) => (
              <li key={s.title}>
                <span className="sd-step-num">{String(i + 1).padStart(2, '0')}</span>
                <div>
                  <strong>{s.title}</strong>
                  <p>{s.text}</p>
                </div>
              </li>
            ))}
          </ol>

          <ul className="sd-skills">
            {skills.map((s) => <li key={s}>{s}</li>)}
          </ul>

          <div className="sd-offer" id="join-team">
            <div className="sd-offer-top">
              <span className="sd-offer-tag">3-month program</span>
              <span className="sd-offer-save">Save {formatInr(savings)}</span>
            </div>
            <div className="sd-offer-price">
              <strong>{formatInr(SKILL_DEVELOPMENT.priceInr)}</strong>
              <s>{formatInr(SKILL_DEVELOPMENT.listPriceInr)}</s>
              <span>{discountPct}% off</span>
            </div>
            <ul className="sd-offer-includes">
              {includes.map((item) => (
                <li key={item}><CheckCircleRoundedIcon sx={{ fontSize: 18 }} /> {item}</li>
              ))}
            </ul>
            <Link to={`/billing/${SKILL_DEVELOPMENT.slug}`} className="btn-hero-primary sd-offer-btn">
              Join the team <ArrowForwardIcon sx={{ fontSize: 18 }} />
            </Link>
            <span className="sd-seats"><i /> Limited seats available</span>
          </div>
        </div>
      </div>
    </div>
  </section>
);

export default SkillDevelopment;
